import { ReactNode, useContext, useMemo } from "react";
import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { PanelAppearanceTable, StyledPanel } from "./StyledPanel";
import { PanelAppearance } from "./PanelAppearance";
import { PanelContext } from "./PanelContext";
import { TabbedContainer, TabbedContainerProps } from "../TabbedContainer";
import styled from "styled-components";

/** The `Panel` component properties. */
export interface PanelProps extends BaseProps {

  /** Specifies the panel appearance. If not specified defaults to "auto". */
  readonly appearance?: PanelAppearance;

  /** The panel aria role. */
  readonly role?: string;

  /** The component children. */
  readonly children?: ReactNode;
}

/**
 * Gets the level of the currently containing panel.
 * @returns The containing panel level.
 */
export function usePanelLevel(): "none" | Omit<PanelAppearance, "auto"> {
  const parentPanelContext = useContext(PanelContext);

  if (parentPanelContext.level === -1) return "none";
  if (parentPanelContext.level === 0) return "primary";

  return "secondary";
}

/** A simple container panel. */
export const Panel = (props: PanelProps) => {
  const { children, role } = props;
  let { appearance = "auto" } = props;

  const parentPanelLevel = usePanelLevel();

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: ["panel"],
    testIdPrefix: "panel",
  });

  if (appearance === "auto") {
    if (parentPanelLevel === "none") appearance = "primary";
    else appearance = "secondary";
  }

  const contextData = useMemo(() => {
    if (appearance === "primary") return { level: 0 };

    return { level: 1 };
  }, [appearance]);

  return (
    <PanelContext.Provider value={contextData}>
      <StyledPanel appearance={PanelAppearanceTable[appearance]} role={role} {...baseHtmlProps}>{children}</StyledPanel>
    </PanelContext.Provider>
  );
};

const StyledTabbedPanel = styled(Panel)`
  padding: 0;
`;

const StyledTabbedContainer = styled(TabbedContainer)`
  background-color: transparent;

  &>.tab-set {
    background-color: transparent;
  }

  &>.tab-panel {
    padding: 1.5rem;
    background-color: transparent;
  }
`;

/** A variant of the `Panel` component which has tabs. */
const TabbedPanel = (props: TabbedContainerProps & PanelProps) => {
  const { className, testId, style, appearance, role, children, ...tabbedContainerProps } = props;

  const baseHtmlProps = getHtmlPropsFromBaseProps({ className, testId, style }, {
    className: "tabbed-panel",
  });

  return (
    <StyledTabbedPanel {...baseHtmlProps} appearance={appearance} role={role}>
      <StyledTabbedContainer {...tabbedContainerProps}>
        {children}
      </StyledTabbedContainer>
    </StyledTabbedPanel>
  );
};

/** An individual pane in the tabbed panel. */
TabbedPanel.TabPane = TabbedContainer.TabPane;

/** A variant of the `Panel` component which has tabs. */
Panel.Tabbed = TabbedPanel;
